import React, { useEffect, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DashboardCard } from '../components/dashboard/DashboardCard';
import { CardBrandsTable } from '../components/dashboard/CardBrandsTable';
import { CardBrandsChart } from '../components/dashboard/CardBrandsChart';
import { KpiCard } from '../components/dashboard/KpiCard';
import { TabView } from '../components/dashboard/TabView';
import { GeoPaymentMap } from '../components/dashboard/GeoPaymentMap';
import { CardErrorCodes } from '../components/dashboard/CardErrorCodes';
import { MetricChart } from '../components/dashboard/MetricChart';
import { ListWidget } from '../components/dashboard/ListWidget';
import { CompanySelector, Company } from '../components/dashboard/CompanySelector';
import { AttentionNeededWidget } from '../components/dashboard/AttentionNeededWidget';
import { RecommendationsWidget } from '../components/dashboard/RecommendationsWidget';
import { BalanceSnapshotWidget } from '../components/dashboard/BalanceSnapshotWidget';
import { DateRangePicker } from '../components/dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../components/dashboard/GranularitySelector';
import { ComparisonSelector, ComparisonType } from '../components/dashboard/ComparisonSelector';
import { PaymentMethodFilter, PaymentMethod } from '../components/dashboard/PaymentMethodFilter';
import { AddChartModal } from '../components/dashboard/AddChartModal';
import { EditChartsModal } from '../components/dashboard/EditChartsModal';
import { CreditCardIcon, ArrowUpRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, FilterIcon, RotateCcwIcon, ReceiptIcon, RefreshCcwIcon } from 'lucide-react';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData, generateDisputeCountData, generatePreviousDisputeCountData, generateDisputeActivityData, generatePreviousDisputeActivityData, generateRefundRateData, generatePreviousRefundRateData, generateNewCustomersData, generatePreviousNewCustomersData, generateBlockedPaymentsData, generatePreviousBlockedPaymentsData, generateBlockedPaymentsVolumeData, generatePreviousBlockedPaymentsVolumeData, generatePreviousAverageTransactionData } from '../components/dashboard/DataGenerator';
export function Overview() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [showAddChartModal, setShowAddChartModal] = useState(false);
  const [showEditChartModal, setShowEditChartModal] = useState(false);
  // Chart order state - defines the order charts appear in
  const [chartOrder, setChartOrder] = useState<string[]>(['gross-volume', 'net-volume', 'successful-payments', 'average-acceptance-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'blocked-payments', 'blocked-payments-volume']);
  // Visible charts state - default to all charts visible
  const [visibleCharts, setVisibleCharts] = useState<Set<string>>(new Set(['gross-volume', 'net-volume', 'successful-payments', 'average-acceptance-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'blocked-payments', 'blocked-payments-volume']));
  // New filter states
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7)),
    end: new Date()
  });
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [comparison, setComparison] = useState<ComparisonType>('previous_period');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentVolumeData, setPaymentVolumeData] = useState([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState([]);
  const [transactionCountData, setTransactionCountData] = useState([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState([]);
  const [averageTransactionData, setAverageTransactionData] = useState([]);
  const [previousAverageTransactionData, setPreviousAverageTransactionData] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [attemptsData, setAttemptsData] = useState([]);
  const [geoPaymentData, setGeoPaymentData] = useState([]);
  const [refundData, setRefundData] = useState([]);
  const [previousRefundData, setPreviousRefundData] = useState([]);
  const [pendingRefundsData, setPendingRefundsData] = useState([]);
  const [refundReasonsData, setRefundReasonsData] = useState([]);
  const [disputeCountData, setDisputeCountData] = useState([]);
  const [previousDisputeCountData, setPreviousDisputeCountData] = useState([]);
  const [disputeActivityData, setDisputeActivityData] = useState([]);
  const [previousDisputeActivityData, setPreviousDisputeActivityData] = useState([]);
  const [refundRateData, setRefundRateData] = useState([]);
  const [previousRefundRateData, setPreviousRefundRateData] = useState([]);
  const [newCustomersData, setNewCustomersData] = useState([]);
  const [previousNewCustomersData, setPreviousNewCustomersData] = useState([]);
  const [blockedPaymentsData, setBlockedPaymentsData] = useState([]);
  const [previousBlockedPaymentsData, setPreviousBlockedPaymentsData] = useState([]);
  const [blockedPaymentsVolumeData, setBlockedPaymentsVolumeData] = useState([]);
  const [previousBlockedPaymentsVolumeData, setPreviousBlockedPaymentsVolumeData] = useState([]);
  // Convert dateRange to timeRange string for data generators
  const getTimeRangeFromDates = (start: Date, end: Date): string => {
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    // Check if it's today
    const today = new Date();
    const isToday = start.toDateString() === today.toDateString() && end.toDateString() === today.toDateString();
    if (isToday) return 'today';
    // Map to closest timeRange
    if (diffDays <= 7) return '7d';
    if (diffDays <= 30) return '30d';
    return '1y';
  };
  // Load companies on component mount
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    setSelectedCompanies(companiesData);
  }, []);
  // Render company selector in sidebar
  useEffect(() => {
    const sidebarContainer = document.getElementById('sidebar-company-selector');
    if (sidebarContainer && companies.length > 0) {
      ReactDOM.render(<CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-7419" />, sidebarContainer);
    }
    return () => {
      if (sidebarContainer) {
        ReactDOM.unmountComponentAtNode(sidebarContainer);
      }
    };
  }, [companies, selectedCompanies]);
  // Update data when filters change
  useEffect(() => {
    console.group('🔄 Overview Data Update');
    console.log('📅 Date range:', dateRange);
    console.log('📅 Date range start type:', typeof dateRange.start, dateRange.start instanceof Date);
    console.log('📅 Date range end type:', typeof dateRange.end, dateRange.end instanceof Date);
    console.log('📅 Date range start value:', dateRange.start);
    console.log('📅 Date range end value:', dateRange.end);
    console.log('⏱️ Granularity:', granularity);
    console.log('🏢 Selected companies:', selectedCompanies);
    // CRITICAL: Ensure dates are actual Date objects
    const startDate = dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start);
    const endDate = dateRange.end instanceof Date ? dateRange.end : new Date(dateRange.end);
    console.log('✅ Validated dates:', {
      startDate,
      endDate
    });
    // FIXED: Pass validated Date objects directly
    const rawPaymentVolumeData = generatePaymentVolumeData('',
    // Empty string to force date-based generation
    startDate, endDate);
    console.log('💰 Raw payment volume data:', rawPaymentVolumeData);
    console.log('💰 First data point:', rawPaymentVolumeData[0]);
    console.log('💰 Last data point:', rawPaymentVolumeData[rawPaymentVolumeData.length - 1]);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, '');
    const rawTransactionCountData = generateTransactionCountData('', startDate, endDate);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, '');
    const rawAverageTransactionData = generateAverageTransactionData('', startDate, endDate);
    const rawPreviousAverageTransactionData = generatePreviousAverageTransactionData(rawAverageTransactionData, '');
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawConversionData = generateConversionData();
    const rawAttemptsData = generateAttemptsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData('', startDate, endDate);
    const rawPreviousRefundData = generatePreviousRefundData(rawRefundData, '');
    const rawPendingRefundsData = generatePendingRefundsData();
    const rawRefundReasonsData = generateRefundReasonsData();
    // Generate new data for missing charts
    const rawDisputeCountData = generateDisputeCountData('', startDate, endDate);
    const rawPreviousDisputeCountData = generatePreviousDisputeCountData(rawDisputeCountData, '');
    const rawDisputeActivityData = generateDisputeActivityData('', startDate, endDate);
    const rawPreviousDisputeActivityData = generatePreviousDisputeActivityData(rawDisputeActivityData, '');
    const rawRefundRateData = generateRefundRateData('', startDate, endDate);
    const rawPreviousRefundRateData = generatePreviousRefundRateData(rawRefundRateData, '');
    const rawNewCustomersData = generateNewCustomersData('', startDate, endDate);
    const rawPreviousNewCustomersData = generatePreviousNewCustomersData(rawNewCustomersData, '');
    const rawBlockedPaymentsData = generateBlockedPaymentsData('', startDate, endDate);
    const rawPreviousBlockedPaymentsData = generatePreviousBlockedPaymentsData(rawBlockedPaymentsData, '');
    const rawBlockedPaymentsVolumeData = generateBlockedPaymentsVolumeData('', startDate, endDate);
    const rawPreviousBlockedPaymentsVolumeData = generatePreviousBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData, '');
    const selectedCompanyIds = selectedCompanies.map(company => company.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length;
    console.log('🏢 Selected company IDs:', selectedCompanyIds);
    console.log('📊 Is all companies:', isAllCompanies);
    if (isAllCompanies || selectedCompanyIds.length === 0) {
      console.log('✅ Using raw data (all companies selected)');
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPreviousAverageTransactionData(rawPreviousAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setConversionData(rawConversionData);
      setAttemptsData(rawAttemptsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setPreviousRefundData(rawPreviousRefundData);
      setPendingRefundsData(rawPendingRefundsData);
      setRefundReasonsData(rawRefundReasonsData);
      setDisputeCountData(rawDisputeCountData);
      setPreviousDisputeCountData(rawPreviousDisputeCountData);
      setDisputeActivityData(rawDisputeActivityData);
      setPreviousDisputeActivityData(rawPreviousDisputeActivityData);
      setRefundRateData(rawRefundRateData);
      setPreviousRefundRateData(rawPreviousRefundRateData);
      setNewCustomersData(rawNewCustomersData);
      setPreviousNewCustomersData(rawPreviousNewCustomersData);
      setBlockedPaymentsData(rawBlockedPaymentsData);
      setPreviousBlockedPaymentsData(rawPreviousBlockedPaymentsData);
      setBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData);
      setPreviousBlockedPaymentsVolumeData(rawPreviousBlockedPaymentsVolumeData);
    } else {
      console.log('🔧 Filtering data by selected companies');
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPreviousAverageTransactionData(filterDataByCompanies(rawPreviousAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData);
      setConversionData(rawConversionData);
      setAttemptsData(filterDataByCompanies(rawAttemptsData, selectedCompanyIds));
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setPreviousRefundData(filterDataByCompanies(rawPreviousRefundData, selectedCompanyIds));
      setPendingRefundsData(filterDataByCompanies(rawPendingRefundsData, selectedCompanyIds));
      setRefundReasonsData(rawRefundReasonsData);
      setDisputeCountData(filterDataByCompanies(rawDisputeCountData, selectedCompanyIds));
      setPreviousDisputeCountData(filterDataByCompanies(rawPreviousDisputeCountData, selectedCompanyIds));
      setDisputeActivityData(filterDataByCompanies(rawDisputeActivityData, selectedCompanyIds));
      setPreviousDisputeActivityData(filterDataByCompanies(rawPreviousDisputeActivityData, selectedCompanyIds));
      setRefundRateData(filterDataByCompanies(rawRefundRateData, selectedCompanyIds));
      setPreviousRefundRateData(filterDataByCompanies(rawPreviousRefundRateData, selectedCompanyIds));
      setNewCustomersData(filterDataByCompanies(rawNewCustomersData, selectedCompanyIds));
      setPreviousNewCustomersData(filterDataByCompanies(rawPreviousNewCustomersData, selectedCompanyIds));
      setBlockedPaymentsData(filterDataByCompanies(rawBlockedPaymentsData, selectedCompanyIds));
      setPreviousBlockedPaymentsData(filterDataByCompanies(rawPreviousBlockedPaymentsData, selectedCompanyIds));
      setBlockedPaymentsVolumeData(filterDataByCompanies(rawBlockedPaymentsVolumeData, selectedCompanyIds));
      setPreviousBlockedPaymentsVolumeData(filterDataByCompanies(rawPreviousBlockedPaymentsVolumeData, selectedCompanyIds));
    }
    console.groupEnd();
  }, [selectedCompanies, companies, dateRange, granularity, comparison, paymentMethods]);
  // Calculate metrics
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  const avgTransaction = totalVolume / totalTransactions;
  const conversionRate = conversionData.length > 2 ? conversionData[2].value / conversionData[0].value * 100 : 0;
  const singleAttemptRate = attemptsData.length > 0 ? attemptsData[0].count / attemptsData.reduce((sum, item) => sum + item.count, 0) * 100 : 0;
  const avgAttempts = attemptsData.length > 0 ? attemptsData.reduce((sum, item, index) => {
    const attemptNumber = index === 3 ? 4 : index + 1;
    return sum + item.count * attemptNumber;
  }, 0) / attemptsData.reduce((sum, item) => sum + item.count, 0) : 0;
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const totalRefundCount = refundData.reduce((sum, item) => sum + item.count, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  const formatDate = (date, granularity) => {
    const dateObj = new Date(date);
    // Ensure valid date
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    if (granularity === 'hourly') {
      return dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (granularity === 'daily') {
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } else if (granularity === 'weekly') {
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } else if (granularity === 'monthly') {
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    } else {
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };
  const formatYAxis = value => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(0)}Mn`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    } else if (value > 0 && value < 1) {
      return value.toFixed(1);
    }
    return value.toString();
  };
  const formatPercentage = value => {
    return `${value}%`;
  };
  const getUpdateTime = () => {
    const now = new Date();
    const minutes = Math.floor(Math.random() * 10) + 1;
    return `Updated ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  };
  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(2);
  };
  const formatCurrency = value => {
    if (value >= 1000000) {
      return `MYR ${(value / 1000000).toFixed(2)}Mn`;
    } else if (value >= 1000) {
      return `MYR ${(value / 1000).toFixed(2)}K`;
    }
    return `MYR ${value.toFixed(2)}`;
  };
  const formatCount = value => {
    return Math.round(value);
  };
  const getTimeRangeTitle = () => {
    return `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`;
  };
  const getXAxisKey = () => {
    return granularity === 'hourly' ? 'hour' : 'date';
  };
  const isAggregated = selectedCompanies.length > 1 && selectedCompanies.length < companies.length;
  // Table components
  const PaymentVolumeTable = () => <div className="overflow-x-auto" data-id="element-7420">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7421">
        <thead className="bg-gray-50" data-id="element-7422">
          <tr data-id="element-7423">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7424">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7425">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7426">
          {paymentVolumeData.map((row, index) => <tr key={index} data-id="element-7427">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7428">
                {formatDate(row.date, granularity)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7429">
                RM {row.amount.toLocaleString('en-MY')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  const TransactionCountTable = () => <div className="overflow-x-auto" data-id="element-7430">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7431">
        <thead className="bg-gray-50" data-id="element-7432">
          <tr data-id="element-7433">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7434">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7435">
              Transactions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7436">
          {transactionCountData.map((row, index) => <tr key={index} data-id="element-7437">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7438">
                {formatDate(row.date, granularity)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7439">
                {row.count.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  const PaymentMethodsTable = () => <div className="overflow-x-auto" data-id="element-7440">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7441">
        <thead className="bg-gray-50" data-id="element-7442">
          <tr data-id="element-7443">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7444">
              Payment Method
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7445">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7446">
          {paymentMethodsData.map((row, index) => <tr key={index} data-id="element-7447">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7448">
                {row.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7449">
                {row.value}%
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  const RefundDataTable = () => <div className="overflow-x-auto" data-id="element-7450">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7451">
        <thead className="bg-gray-50" data-id="element-7452">
          <tr data-id="element-7453">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7454">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7455">
              Refund Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7456">
              Count
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7457">
          {refundData.map((row, index) => <tr key={index} data-id="element-7458">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7459">
                {formatDate(row.date, granularity)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7460">
                RM {row.amount.toLocaleString('en-MY')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7461">
                {row.count}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  const GeoPaymentTable = () => <div className="overflow-x-auto" data-id="element-7462">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7463">
        <thead className="bg-gray-50" data-id="element-7464">
          <tr data-id="element-7465">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7466">
              Country
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7467">
              Transactions
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7468">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7469">
          {geoPaymentData.map((row, index) => <tr key={index} data-id="element-7470">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7471">
                {row.country}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7472">
                {row.transactions.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7473">
                RM {row.amount.toLocaleString('en-MY')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  const handleAddCharts = (chartIds: string[]) => {
    setVisibleCharts(new Set(chartIds));
  };
  const handleEditCharts = (newChartOrder: string[], newVisibleCharts: Set<string>) => {
    setChartOrder(newChartOrder);
    setVisibleCharts(newVisibleCharts);
  };
  // Helper function to check if a chart should be visible
  const isChartVisible = (chartId: string) => {
    return visibleCharts.has(chartId);
  };
  // Chart metadata for Edit modal
  const chartMetadata = new Map([['gross-volume', {
    id: 'gross-volume',
    title: 'Gross volume',
    value: formatCurrency(totalVolume)
  }], ['net-volume', {
    id: 'net-volume',
    title: 'Net volume',
    value: formatCurrency(totalVolume * 0.98)
  }], ['successful-payments', {
    id: 'successful-payments',
    title: 'Successful payments',
    value: totalTransactions.toLocaleString()
  }], ['average-acceptance-rate', {
    id: 'average-acceptance-rate',
    title: 'Payment success rate',
    value: `${conversionRate.toFixed(1)}%`
  }], ['refunds-volume', {
    id: 'refunds-volume',
    title: 'Refunds volume',
    value: formatCurrency(totalRefundAmount)
  }], ['refund-rate', {
    id: 'refund-rate',
    title: 'Refund rate',
    value: `${refundPercentage.toFixed(1)}%`
  }], ['total-payments', {
    id: 'total-payments',
    title: 'Total payments'
  }], ['dispute-count', {
    id: 'dispute-count',
    title: 'Dispute count',
    value: '42'
  }], ['dispute-activity', {
    id: 'dispute-activity',
    title: 'Dispute activity',
    value: '1.2%'
  }], ['disputed-payments', {
    id: 'disputed-payments',
    title: 'Disputed payments'
  }], ['failed-payments', {
    id: 'failed-payments',
    title: 'Failed payments'
  }], ['payment-methods', {
    id: 'payment-methods',
    title: 'Top payment method categories'
  }], ['new-customers', {
    id: 'new-customers',
    title: 'New customers',
    value: '287'
  }], ['spend-per-customer', {
    id: 'spend-per-customer',
    title: 'Spend per customer',
    value: formatCurrency(avgTransaction)
  }], ['top-customers', {
    id: 'top-customers',
    title: 'Top customers by spend'
  }], ['blocked-payments', {
    id: 'blocked-payments',
    title: 'Blocked payments',
    value: '68'
  }], ['blocked-payments-volume', {
    id: 'blocked-payments-volume',
    title: 'Blocked payments volume',
    value: 'MYR 18.5K'
  }]]);
  // Helper function to render a chart by ID
  const renderChart = (chartId: string) => {
    switch (chartId) {
      case 'gross-volume':
        return <MetricChart key={chartId} title="Gross volume" tooltipText="Estimated amount for captured payments, shown in your default settlement currency using average exchange rates over the selected time period. Gross sales figures include payments that were later disputed or refunded." currentValue={formatCurrency(totalVolume)} growthPercent={parseFloat(calculateGrowth(totalVolume, totalVolume * 0.92))} previousValue={formatCurrency(totalVolume * 0.92)} data={paymentVolumeData} previousData={previousPaymentVolumeData} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `MYR ${value.toLocaleString('en-MY')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7474" />;
      case 'net-volume':
        return <MetricChart key={chartId} title="Net volume" tooltipText="Estimated volume from payments that are settled to your balance after fees, refunds, disputes, and transfers have been deducted." currentValue={formatCurrency(totalVolume * 0.98)} growthPercent={parseFloat(calculateGrowth(totalVolume * 0.98, totalVolume * 0.9))} previousValue={formatCurrency(totalVolume * 0.9)} data={paymentVolumeData} previousData={previousPaymentVolumeData} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `MYR ${value.toLocaleString('en-MY')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7475" />;
      case 'successful-payments':
        return <MetricChart key={chartId} title="Successful payments" tooltipText="The number of successful payments over the given time period." currentValue={totalTransactions.toLocaleString()} growthPercent={parseFloat(calculateGrowth(totalTransactions, totalTransactions * 0.94))} previousValue={`${formatCount(totalTransactions * 0.94).toLocaleString()}`} data={transactionCountData} previousData={previousTransactionCountData} dataKey="count" tooltipValueFormatter={value => `${value.toLocaleString()}`} tooltipLabel="payments" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7476" />;
      case 'average-acceptance-rate':
        return <MetricChart key={chartId} title="Payment success rate" tooltipText="The payment success rate is a measure of how many payment attempts are successfully accepted by payment providers, such as card issuers or local payment methods." currentValue={`${conversionRate.toFixed(1)}%`} growthPercent={parseFloat(calculateGrowth(conversionRate, conversionRate * 0.96))} previousValue={`${(conversionRate * 0.96).toFixed(1)}%`} data={paymentVolumeData.map((item, i) => ({
          date: item.date,
          rate: parseFloat((72 + Math.random() * 5).toFixed(1))
        }))} previousData={paymentVolumeData.map((item, i) => ({
          date: item.date,
          rate: parseFloat((69 + Math.random() * 5).toFixed(1))
        }))} dataKey="rate" yAxisFormatter={formatPercentage} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7477" />;
      case 'refunds-volume':
        return <MetricChart key={chartId} title="Refunds volume" tooltipText="Amount of refunds conducted over the selected time period. The total reflects an estimated amount converted into your default settlement currency using average exchange rates over the time period." currentValue={formatCurrency(totalRefundAmount)} growthPercent={parseFloat(calculateGrowth(totalRefundAmount, totalRefundAmount * 1.1))} previousValue={formatCurrency(totalRefundAmount * 1.1)} data={refundData} previousData={previousRefundData} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `MYR ${value.toLocaleString('en-MY')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7478" />;
      case 'refund-rate':
        return <MetricChart key={chartId} title="Refund rate" tooltipText="Refund rate represents the total number of payments that have partial and full refunds divided by the total number of successful payments in the same period." currentValue={`${refundPercentage.toFixed(1)}%`} growthPercent={parseFloat(calculateGrowth(refundPercentage, refundPercentage * 1.05))} previousValue={`${(refundPercentage * 1.05).toFixed(1)}%`} data={refundRateData} previousData={previousRefundRateData} dataKey="rate" yAxisFormatter={formatPercentage} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7479" />;
      case 'total-payments':
        return <ListWidget key={chartId} title="Total payments" tooltipText="Snapshot of the latest status for all payment intents created in the selected time period." items={[{
          label: 'Succeeded',
          value: '48,490'
        }, {
          label: 'Authorized',
          value: '3,109'
        }, {
          label: 'Failed',
          value: '2,160'
        }, {
          label: 'Refunded & Partially refunded',
          value: '11,094'
        }, {
          label: 'Refund failed',
          value: '114'
        }]} updateTime={getUpdateTime()} data-id="element-7480" />;
      case 'dispute-count':
        return <MetricChart key={chartId} title="Dispute count" tooltipText="Estimated number of disputes. RFIs and pre-chargebacks are not included" currentValue="42" growthPercent={12.5} previousValue="37" data={disputeCountData} previousData={previousDisputeCountData} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="disputes" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7481" />;
      case 'dispute-activity':
        return <MetricChart key={chartId} title="Dispute activity" tooltipText="Estimated number of disputes divided by the number of successful payments in the same time series. RFIs and pre-chargebacks are not included in your dispute rate." currentValue="1.2%" growthPercent={-8.3} previousValue="1.3%" data={disputeActivityData} previousData={previousDisputeActivityData} dataKey="rate" yAxisFormatter={formatPercentage} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7482" />;
      case 'disputed-payments':
        return <ListWidget key={chartId} title="Disputed payments" items={[{
          label: <div data-id="element-7484">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7485">
                      MYR 30.50
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-7486">Fraudulent</div>
                  </div>,
          badge: <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded" data-id="element-7487">
                    Requires response
                  </span>
        }, {
          label: <div data-id="element-7488">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7489">
                      MYR 122.25
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-7490">Duplicate</div>
                  </div>,
          badge: <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded" data-id="element-7491">
                    Challenged
                  </span>
        }, {
          label: <div data-id="element-7492">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7493">
                      MYR 16.00
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-7494">Fraudulent</div>
                  </div>,
          badge: <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded" data-id="element-7495">
                    Lost
                  </span>
        }]} updateTime={getUpdateTime()} data-id="element-7483" />;
      case 'failed-payments':
        return <ListWidget key={chartId} title="Failed payments" tooltipText="Most recent payment failures excluding blocked payments." items={[{
          label: <div data-id="element-7497">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7498">
                      MYR 46.00
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-7499">
                      Nov 1, 2:25 PM • ch_3SOYFbLn5RKpbnwG1bUhOE0L
                    </div>
                  </div>,
          badge: <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded" data-id="element-7500">
                    Failed
                  </span>
        }, {
          label: <div data-id="element-7501">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7502">
                      MYR 24.60
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-7503">
                      Aug 3, 2:38 AM • ch_3RrkJuLn5RKpbnwG0IPPvBSI
                    </div>
                  </div>,
          badge: <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded" data-id="element-7504">
                    Failed
                  </span>
        }]} updateTime={getUpdateTime()} data-id="element-7496" />;
      case 'payment-methods':
        return <div key={chartId} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7505">
            <div className="flex items-center gap-2 mb-4" data-id="element-7506">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-7507">
                Top payment method categories
              </h3>
              <button className="text-gray-400 hover:text-gray-600 group relative" data-id="element-7508">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" data-id="element-7509">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" data-id="element-7510" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" data-id="element-7511" />
                </svg>
                <div className="hidden group-hover:block absolute left-0 top-6 w-72 p-3 bg-gray-900 text-white text-xs text-left rounded shadow-lg z-10" data-id="element-7512">
                  Estimated gross volume is the total sales volume per payment
                  method. This includes refunds and disputes.
                </div>
              </button>
            </div>

            <div data-id="element-7513">
              {/* Stacked bar */}
              <div className="mb-4" data-id="element-7514">
                <div className="h-8 flex rounded-lg overflow-hidden" data-id="element-7515">
                  <div className="bg-purple-500" style={{
                  width: '68%'
                }} data-id="element-7516"></div>
                  <div className="bg-cyan-500" style={{
                  width: '22%'
                }} data-id="element-7517"></div>
                  <div className="bg-pink-500" style={{
                  width: '7%'
                }} data-id="element-7518"></div>
                  <div className="bg-orange-500" style={{
                  width: '2%'
                }} data-id="element-7519"></div>
                  <div className="bg-red-800" style={{
                  width: '1%'
                }} data-id="element-7520"></div>
                </div>
              </div>
              {/* List */}
              <div className="space-y-3" data-id="element-7521">
                <div className="flex justify-between items-center px-3 py-2 rounded hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-200" data-id="element-7522">
                  <div className="flex items-center gap-2" data-id="element-7523">
                    <div className="w-3 h-3 rounded-full bg-purple-500" data-id="element-7524"></div>
                    <span className="text-sm text-gray-600" data-id="element-7525">Card</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7526">
                    MYR 240.90K
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 rounded hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-200" data-id="element-7527">
                  <div className="flex items-center gap-2" data-id="element-7528">
                    <div className="w-3 h-3 rounded-full bg-cyan-500" data-id="element-7529"></div>
                    <span className="text-sm text-gray-600" data-id="element-7530">Bank transfer</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7531">
                    MYR 180.50K
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 rounded hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-200" data-id="element-7532">
                  <div className="flex items-center gap-2" data-id="element-7533">
                    <div className="w-3 h-3 rounded-full bg-pink-500" data-id="element-7534"></div>
                    <span className="text-sm text-gray-600" data-id="element-7535">E-wallet</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7536">
                    MYR 95.20K
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 rounded hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-200" data-id="element-7537">
                  <div className="flex items-center gap-2" data-id="element-7538">
                    <div className="w-3 h-3 rounded-full bg-orange-500" data-id="element-7539"></div>
                    <span className="text-sm text-gray-600" data-id="element-7540">Crypto</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7541">
                    MYR 12.30K
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 rounded hover:bg-blue-50 cursor-pointer transition-colors" data-id="element-7542">
                  <div className="flex items-center gap-2" data-id="element-7543">
                    <div className="w-3 h-3 rounded-full bg-red-800" data-id="element-7544"></div>
                    <span className="text-sm text-gray-600" data-id="element-7545">Other</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7546">
                    MYR 5.10K
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4" data-id="element-7547">
              <div className="text-xs text-gray-500" data-id="element-7548">{getUpdateTime()}</div>
              <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7549">
                View more
              </button>
            </div>
          </div>;
      case 'new-customers':
        return <MetricChart key={chartId} title="New customers" tooltipText="Number of new customers created, including customers that are subsequently deleted. This number does not include guest customers." currentValue="287" growthPercent={15.2} previousValue="249" data={newCustomersData} previousData={previousNewCustomersData} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="customers" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7550" />;
      case 'spend-per-customer':
        return <MetricChart key={chartId} title="Spend per customer" tooltipText="Estimated gross volume for all payments created with a customer, divided by the total number of customers with payments." currentValue={formatCurrency(avgTransaction)} growthPercent={5.8} previousValue={formatCurrency(avgTransaction * 0.945)} data={averageTransactionData} previousData={previousAverageTransactionData} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `MYR ${value.toLocaleString('en-MY')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7551" />;
      case 'top-customers':
        return <ListWidget key={chartId} title="Top customers by spend" tooltipText="Estimated gross volume is the total spend per customer, including guest customers. The numbers do not include refunds and disputes." items={[{
          label: 'Rajesh Kumar a/l Subramaniam',
          value: 'MYR 4,523'
        }, {
          label: 'Siti Nurhaliza binti Hassan',
          value: 'MYR 3,845'
        }, {
          label: 'Jimbangan anak Empaling',
          value: 'MYR 2,987'
        }, {
          label: "Marcus D'Cruz",
          value: 'MYR 2,234'
        }, {
          label: 'Tan Wei Ming',
          value: 'MYR 1,892'
        }]} updateTime={getUpdateTime()} data-id="element-7552" />;
      case 'blocked-payments':
        return <MetricChart key={chartId} title="Blocked payments" tooltipText="Count of payments blocked due to risk settings" currentValue="68" growthPercent={22.5} previousValue="55" data={blockedPaymentsData} previousData={previousBlockedPaymentsData} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="blocked" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7553" />;
      case 'blocked-payments-volume':
        return <MetricChart key={chartId} title="Blocked payments volume" tooltipText="Volume of payments blocked due to risk settings" currentValue="MYR 18.5K" growthPercent={18.2} previousValue="MYR 15.6K" data={blockedPaymentsVolumeData} previousData={previousBlockedPaymentsVolumeData} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => `MYR ${value.toLocaleString('en-MY')}`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-7554" />;
      default:
        return null;
    }
  };
  return <div className="space-y-4" data-id="element-7555">
      {/* Page Title */}
      <div className="mb-4" data-id="element-7556">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-7557">Overview</h1>
      </div>

      {/* Static Widgets Section - Always Current */}
      <div className="bg-gray-50 rounded-lg p-4" data-id="element-7558">
        <div className="flex items-center gap-2 mb-3" data-id="element-7559">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-7560">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="text-blue-600" data-id="element-7561">
              <path d="M8 1a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 018 1zM4.75 3.5a.75.75 0 00-1.5 0v9a.75.75 0 001.5 0v-9zM12.25 3.5a.75.75 0 00-1.5 0v9a.75.75 0 001.5 0v-9z" data-id="element-7562" />
            </svg>
            <span data-id="element-7563">Quick Overview</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-id="element-7564">
          <BalanceSnapshotWidget data-id="element-7565" />
          <AttentionNeededWidget data-id="element-7566" />
          <RecommendationsWidget data-id="element-7567" />
        </div>
      </div>

      {/* Filtered Analytics Section */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-7568">
        {/* Section Header with Date Range */}
        <div className="flex items-center justify-between mb-2" data-id="element-7569">
          <div className="flex items-center gap-2" data-id="element-7570">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="text-blue-600" data-id="element-7571">
              <path d="M4.75 2a.75.75 0 00-.75.75v1.5a.75.75 0 001.5 0v-1.5A.75.75 0 004.75 2zM11.25 2a.75.75 0 00-.75.75v1.5a.75.75 0 001.5 0v-1.5a.75.75 0 00-.75-.75zM3.5 5.25a.75.75 0 01.75-.75h7.5a.75.75 0 01.75.75v7.5a.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75v-7.5z" data-id="element-7572" />
            </svg>
            <h2 className="text-sm font-medium text-gray-700" data-id="element-7573">Analytics</h2>
          </div>
        </div>

        {/* Universal Filters Row */}
        <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-7574">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-7575">
            <div className="flex flex-wrap items-center gap-2" data-id="element-7576">
              <DateRangePicker value={dateRange} onChange={setDateRange} data-id="element-7577" />
              <GranularitySelector value={granularity} onChange={setGranularity} data-id="element-7578" />
              <ComparisonSelector value={comparison} onChange={setComparison} data-id="element-7579" />
              <PaymentMethodFilter value={paymentMethods} onChange={setPaymentMethods} data-id="element-7580" />
            </div>
            <div className="flex items-center gap-2" data-id="element-7581">
              <button onClick={() => setShowAddChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-7582">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" data-id="element-7583">
                  <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" data-id="element-7584" />
                </svg>
                Add
              </button>
              <button onClick={() => setShowEditChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-7585">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" data-id="element-7586">
                  <path d="M11.013 2.513a1.75 1.75 0 012.475 2.474l-8.61 8.61a.75.75 0 01-.353.194l-3.75.75a.75.75 0 01-.877-.877l.75-3.75a.75.75 0 01.194-.353l8.61-8.61z" data-id="element-7587" />
                </svg>
                Edit
              </button>
            </div>
          </div>
        </div>

        {/* Aggregation indicator */}
        {isAggregated && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start" data-id="element-7588">
            <FilterIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-7589" />
            <div data-id="element-7590">
              <p className="text-sm text-blue-800" data-id="element-7591">
                <span className="font-medium" data-id="element-7592">Aggregated view:</span> Showing
                combined data for {selectedCompanies.length} companies.
              </p>
              <div className="mt-1 flex flex-wrap gap-1" data-id="element-7593">
                {selectedCompanies.map(company => <span key={company.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-7594">
                    {company.name}
                  </span>)}
              </div>
            </div>
          </div>}

        {/* Charts Grid - 3 columns responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7595">
          {chartOrder.filter(isChartVisible).map(chartId => renderChart(chartId))}
        </div>
      </div>

      {/* Add Chart Modal */}
      {showAddChartModal && <AddChartModal onClose={() => setShowAddChartModal(false)} onAdd={handleAddCharts} initialSelectedCharts={visibleCharts} renderChart={renderChart} data-id="element-7596" />}

      {/* Edit Charts Modal */}
      {showEditChartModal && <EditChartsModal onClose={() => setShowEditChartModal(false)} onSave={handleEditCharts} initialChartOrder={chartOrder} initialVisibleCharts={visibleCharts} chartData={chartMetadata} renderChart={renderChart} data-id="element-7597" />}
    </div>;
}